import React, { useState, useEffect, useLayoutEffect, useContext } from "react";
import "@/App.css";
import "./removeBadge.js";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import axios from "axios";
import InitialPage from "./pages/InitialPage";
import HomePage from "./pages/HomePage";
import ContactPage from "./pages/ContactPage";
import CursoPage from "./pages/CursoPage";
import LoginPage from "./pages/LoginPage";
import CEOLoginPage from "./pages/CEOLoginPage";
import CEOPanel from "./pages/CEOPanel";
import LandingEditor from "./pages/LandingEditor";
import Dashboard from "./pages/Dashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import AssistantDashboard from "./pages/AssistantDashboard";
import ModeloDashboard from "./pages/ModeloDashboard";
import MessagesPage from "./pages/MessagesPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import MonitoreoActivo from "./components/MonitoreoActivo";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BACKEND_URL } from "./config";

const API = `${BACKEND_URL}/api`;

// Configure axios global defaults: send credentials AND Authorization header from localStorage.
// This works around iOS Safari and Chrome incognito dropping third-party cookies.
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Error logging out", error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl font-semibold text-gray-700">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && user.role !== "administrativo" && user.role !== "asistente") {
    return <Navigate to="/empleado" />;
  }

  return children;
}

function AppRoutes() {
  // Rutas públicas: forzar tema oscuro (la landing/login solo se ve bien en oscuro)
  const location = useLocation();
  const publicPaths = ['/', '/landing', '/contact', '/curso', '/login', '/ceo-login', '/privacy-policy', '/terms-of-service'];
  const isPublic = publicPaths.includes(location.pathname);
  
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (isPublic) {
      // Guardar el tema actual y forzar dark
      const wasLight = root.classList.contains('light');
      root.classList.remove('light');
      root.classList.add('dark');
      root.setAttribute('data-public-dark', wasLight ? '1' : '0');
    } else {
      // Restaurar el tema del usuario al salir de una página pública
      const wasLight = root.getAttribute('data-public-dark') === '1';
      if (wasLight) {
        root.classList.remove('dark');
        root.classList.add('light');
        root.removeAttribute('data-public-dark');
      }
    }
  }, [isPublic]);
  
  return (
    <Routes>
      <Route path="/" element={<InitialPage />} />
      <Route path="/landing" element={<HomePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/curso" element={<CursoPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/ceo-login" element={<CEOLoginPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route
        path="/ceo-panel"
        element={
          <ProtectedRoute adminOnly={true}>
            <CEOPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editor"
        element={
          <ProtectedRoute adminOnly={true}>
            <LandingEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute adminOnly={true}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/empleado"
        element={
          <ProtectedRoute>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/asistente"
        element={
          <ProtectedRoute>
            <AssistantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modelo"
        element={
          <ProtectedRoute>
            <ModeloDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRoutes />
              <GlobalMonitoreo />
              <Toaster position="top-right" />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </div>
    </HelmetProvider>
  );
}

// Componente de monitoreo global (fuera del stacking context de los dashboards)
function GlobalMonitoreo() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  
  // Solo mostrar en rutas de empleados
  const rutasMonitoreadas = ['/empleado', '/asistente'];
  const debeMonitorear = user && rutasMonitoreadas.some(r => location.pathname.startsWith(r));
  
  if (!debeMonitorear) return null;
  
  return (
    <MonitoreoActivo
      userId={user?.id}
      userRole={user?.role}
      onInactivo={() => window.location.href = '/'}
    />
  );
}

export default App;
